import * as React from 'react';

import { NavOfferApp } from '@components/nav-offer-app';
import { useAuth } from '@components/auth/AuthContext';
import { useCapabilities } from '@hooks/useCapabilities';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@ui/sidebar';
import { Separator } from '@ui/separator';
import { NavUser } from '@components/nav-user';
import ClearOfferOriginalNoTagline from '@components/ClearOfferNoTagLine';
import { CompanySelector } from '@components/CompanySelector';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const { state } = useSidebar();
  const { hasCapability } = useCapabilities();
  const isCollapsed = state === 'collapsed';

  // Create a formatted user object for NavUser
  const formattedUser = user
    ? {
        name: user.firstName || user.email || 'User',
        email: user.email || '',
        avatar: '', // You can add an avatar URL if available
      }
    : null;

  const currentYear = new Date().getFullYear();

  return (
    <Sidebar
      variant="sidebar"
      collapsible="icon"
      className="shrink-0"
      data-testid="app-sidebar"
      {...props}
    >
      <SidebarHeader className="p-4 group-data-[collapsible=icon]:p-2" data-testid="sidebar-header">
        <div data-testid="sidebar-logo">
          <ClearOfferOriginalNoTagline />
        </div>
      </SidebarHeader>
      <SidebarContent className={isCollapsed ? 'px-1' : 'pl-4'} data-testid="sidebar-content">
        {/* Show company selector for users with cross-company access */}
        {hasCapability('agency.cross_company') && (
          <CompanySelector className={isCollapsed ? 'hidden' : ''} />
        )}
        <NavOfferApp />
      </SidebarContent>
      <Separator data-testid="sidebar-separator" />
      {formattedUser && (
        <div className="mt-auto" data-testid="user-section">
          <NavUser user={formattedUser} />
        </div>
      )}
      <SidebarFooter className={isCollapsed ? 'p-1' : 'p-4'} data-testid="sidebar-footer">
        {!isCollapsed && (
          <p className="text-xs text-muted-foreground" data-testid="copyright-text">
            © {currentYear} ClearOffer
          </p>
        )}
      </SidebarFooter>
      <SidebarRail data-testid="sidebar-rail" />
    </Sidebar>
  );
}
